import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Card, Typography, Popconfirm, message, Spin,
  Alert, Tag, Input, Select, Badge, Tooltip, DatePicker, Row, Col, Statistic
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  SearchOutlined, ReloadOutlined, StopOutlined, CheckCircleOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, AppstoreOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { schoolAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import { formatAreaText } from '../../utils/areaHelpers';

const { Title, Text } = Typography;
const { Option } = Select;

// 状态配置
const STATUS_CONFIG = {
  active:   { label: '正常运营', color: 'success',   icon: <CheckCircleOutlined /> },
  inactive: { label: '已停用',   color: 'error',     icon: <StopOutlined /> },
  trial:    { label: '试用中',   color: 'processing', icon: <ClockCircleOutlined /> },
  expired:  { label: '已到期',   color: 'warning',   icon: <ExclamationCircleOutlined /> },
};

// 套餐配置
const PLAN_CONFIG = {
  free:       { label: '免费版',   color: 'default' },
  basic:      { label: '基础版',   color: 'blue' },
  pro:        { label: '专业版',   color: 'purple' },
  enterprise: { label: '企业版',   color: 'gold' },
};

function buildGeoStatsFromSchools(data) {
  const provinceMap = new Map();
  const cityMap = new Map();
  const countyMap = new Map();
  let unassignedCount = 0;

  data.forEach((school) => {
    if (school.province) {
      provinceMap.set(school.province, (provinceMap.get(school.province) || 0) + 1);
    }
    if (school.city) {
      cityMap.set(school.city, (cityMap.get(school.city) || 0) + 1);
    }
    if (school.county) {
      countyMap.set(school.county, (countyMap.get(school.county) || 0) + 1);
    }
    if (!school.province || !school.city || !school.county) {
      unassignedCount += 1;
    }
  });

  const toSortedList = (map) =>
    Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name));

  return {
    overview: {
      totalSchools: data.length,
      coveredProvinceCount: provinceMap.size,
      coveredCityCount: cityMap.size,
      coveredCountyCount: countyMap.size,
      unassignedCount
    },
    provinceStats: toSortedList(provinceMap),
    cityStats: toSortedList(cityMap),
    countyStats: toSortedList(countyMap)
  };
}

const SchoolList = () => {
  const { isSuperAdmin } = useAuth();
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, trial: 0, expired: 0 });
  const [geoStats, setGeoStats] = useState(buildGeoStatsFromSchools([]));
  const navigate = useNavigate();
  const isPlatformAdmin = Boolean(user && (
    user.username === 'admin' ||
    user.role === 'super_admin' ||
    user.role === 'platform_admin'
  ));

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: 200 };
      if (statusFilter) params.status = statusFilter;
      if (planFilter) params.plan_type = planFilter;
      if (searchKeyword) params.search = searchKeyword;

      const [schoolResponse, geoResponse] = await Promise.allSettled([
        schoolAPI.getAll(params),
        schoolAPI.getGeoStats()
      ]);

      if (schoolResponse.status !== 'fulfilled') {
        throw schoolResponse.reason;
      }

      const data = schoolResponse.value.data.data || [];
      const summary = schoolResponse.value.data.summary || {};
      setSchools(data);

      // 实时统计从后端返回的 summary 获取真实数据库全量汇总
      const s = {
        total: summary.totalSchools ?? data.length,
        active: summary.activeSchools ?? 0,
        trial: data.filter(school => school.status === 'trial').length,
        inactive: summary.inactiveSchools ?? 0,
        expired: data.filter(school => school.status === 'expired').length
      };
      setStats(s);

      if (geoResponse.status === 'fulfilled' && geoResponse.value.data.success) {
        setGeoStats(geoResponse.value.data.data || buildGeoStatsFromSchools(data));
      } else {
        setGeoStats(buildGeoStatsFromSchools(data));
      }
    } catch (err) {
      console.error('获取学校列表失败:', err);
      setError('获取学校列表失败，请检查网络连接或重新登录');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, planFilter, searchKeyword]);

  useEffect(() => { fetchSchools(); }, [fetchSchools]);

  const handleDelete = async (id, name) => {
    try {
      await schoolAPI.delete(id);
      message.success(`学校 "${name}" 已删除`);
      fetchSchools();
    } catch (err) {
      message.error('删除学校失败');
    }
  };

  const handleToggleStatus = async (record, newStatus) => {
    try {
      await schoolAPI.toggleStatus(record.id, newStatus);
      const label = STATUS_CONFIG[newStatus]?.label || newStatus;
      message.success(`学校 "${record.name}" 状态已更新为：${label}`);
      fetchSchools();
    } catch (err) {
      message.error('状态更新失败');
    }
  };



  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: '学校名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => navigate(`/schools/${record.id}`)} style={{ fontWeight: 500 }}>
          {text}
        </a>
      ),
    },
    {
      title: '地区归属',
      key: 'region',
      width: 180,
      render: (_, record) => {
        const region = formatAreaText(record.province, record.city, record.county);
        return region || <Text type="secondary">未规划 / 未完整</Text>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
        return <Badge status={cfg.color} text={cfg.label} />;
      },
    },
    {
      title: '套餐',
      dataIndex: 'plan_type',
      key: 'plan_type',
      width: 100,
      render: (plan) => {
        const cfg = PLAN_CONFIG[plan] || PLAN_CONFIG.free;
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: '到期日期',
      dataIndex: 'expire_date',
      key: 'expire_date',
      width: 120,
      render: (date) => {
        if (!date) return <Text type="secondary">永久</Text>;
        const isExpiring = dayjs(date).diff(dayjs(), 'day') <= 30;
        return (
          <Text type={isExpiring ? 'danger' : 'secondary'}>
            {dayjs(date).format('YYYY-MM-DD')}
          </Text>
        );
      },
    },
    {
      title: '班级/教师',
      key: 'counts',
      width: 110,
      render: (_, record) => (
        <Text type="secondary">
          {record.class_count || 0} 班 / {record.teacher_count || 0} 师
        </Text>
      ),
    },
    {
      title: '联系人',
      dataIndex: 'principal',
      key: 'principal',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      render: (_, record) => {
        const isActive = record.status === 'active' || record.status === 'trial';
        return (
          <Space size={4} wrap>
            {isSuperAdmin() && <Button type="link" size="small" onClick={() => navigate(`/schools/${record.id}?tab=administrators`)}>管理员设置</Button>}
            <Tooltip title="查看详情">
              <Button
                type="link" size="small" icon={<EyeOutlined />}
                onClick={() => navigate(`/schools/${record.id}`)}
              />
            </Tooltip>
            <Tooltip title="编辑信息">
              <Button
                type="link" size="small" icon={<EditOutlined />}
                onClick={() => navigate(`/schools/${record.id}/edit`)}
              />
            </Tooltip>
            <Tooltip title="模块管理">
              <Button
                type="link" size="small" icon={<AppstoreOutlined />}
                onClick={() => navigate(`/schools/${record.id}?tab=modules`)}
              />
            </Tooltip>
            <Tooltip title={isActive ? '停用学校' : '启用学校'}>
              <Popconfirm
                title={isActive ? `确定停用 "${record.name}" 吗？停用后该校所有功能将不可用` : `确定启用 "${record.name}" 吗？`}
                onConfirm={() => handleToggleStatus(record, isActive ? 'inactive' : 'active')}
                okText="确定" cancelText="取消"
                okType={isActive ? 'danger' : 'primary'}
              >
                <Button
                  type="link" size="small"
                  icon={isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                  danger={isActive}
                />
              </Popconfirm>
            </Tooltip>
            <Tooltip title="删除学校">
              <Popconfirm
                title={`确定删除 "${record.name}" 吗？数据将保留30天后自动清理`}
                onConfirm={() => handleDelete(record.id, record.name)}
                okText="确定删除" cancelText="取消"
                okType="danger"
              >
                <Button type="link" size="small" icon={<DeleteOutlined />} danger />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* 统计概览 */}
      <Row gutter={16}>
        <Col span={4}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="学校总数" value={stats.total} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="正常运营" value={stats.active} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="试用中" value={stats.trial} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="已停用" value={stats.inactive} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="已到期" value={stats.expired} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="已覆盖省份" value={geoStats.overview?.coveredProvinceCount || 0} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="已覆盖城市" value={geoStats.overview?.coveredCityCount || 0} valueStyle={{ color: '#13c2c2' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="已覆盖区县" value={geoStats.overview?.coveredCountyCount || 0} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="未完成归属" value={geoStats.overview?.unassignedCount || 0} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
      </Row>

      {/* 列表卡片 */}
      <Card
        title={<Title level={4} style={{ margin: 0 }}>学校管理</Title>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchSchools} loading={loading}>
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/schools/create')}
            >
              添加学校
            </Button>
          </Space>
        }
      >
        {/* 筛选栏 */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            placeholder="搜索学校名称/地区/联系人/电话"
            style={{ width: 280 }}
            allowClear
            enterButton
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              if (!e.target.value) {
                setSearchKeyword('');
              }
            }}
            onSearch={(value) => {
              setSearchKeyword(value.trim());
            }}
          />
          <Select
            placeholder="按状态筛选"
            style={{ width: 130 }}
            allowClear
            value={statusFilter || undefined}
            onChange={setStatusFilter}
          >
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <Option key={k} value={k}>{v.label}</Option>
            ))}
          </Select>
          <Select
            placeholder="按套餐筛选"
            style={{ width: 120 }}
            allowClear
            value={planFilter || undefined}
            onChange={setPlanFilter}
          >
            {Object.entries(PLAN_CONFIG).map(([k, v]) => (
              <Option key={k} value={k}>{v.label}</Option>
            ))}
          </Select>
        </Space>

        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ marginRight: 12 }}>省份分布 TOP 5</Text>
          <Space wrap>
            {(geoStats.provinceStats || []).slice(0, 5).map((item) => (
              <Tag key={item.name} color="blue">
                {item.name}: {item.count}
              </Tag>
            ))}
            {(!geoStats.provinceStats || geoStats.provinceStats.length === 0) && (
              <Text type="secondary">暂无地区统计数据</Text>
            )}
          </Space>
        </div>

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={schools}
            rowKey="id"
            size="middle"
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ['15', '30', '50', '100'],
              showTotal: (total) => `共 ${total} 所学校`
            }}
            rowClassName={(record) => record.status === 'inactive' ? 'row-disabled' : ''}
          />
        </Spin>
      </Card>

      <style>{`
        .row-disabled { opacity: 0.55; }
      `}</style>
    </Space>
  );
};

export default SchoolList;
